import { inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AddItemWishListRequest } from '../interfaces/add-item-wish-list-request.interface';
import { catchError, map, Observable, of } from 'rxjs';
import { ModalService } from './modal.service';
import { CreateWishListRequest } from '../interfaces/create-wish-list-request.interface';
import { CatalogOptionResponse } from '../interfaces/catalog-option-response.interface';
import { GetWishListDetailResponse } from '../interfaces/get-wish-list-detail-response.interface';
import { MoveItemWishListRequest } from '../interfaces/move-item-wish-list-request.interface';
import { ShareWishlistRequestInterface } from '../interfaces/share-wishlist-request.interface';
import { Router } from '@angular/router';
import { ValidateSharedTokenRequest } from '../../external-home/interfaces/validate-shared-token.request';
import { ValidateSharedWishlistUrl } from '../../external-home/interfaces/validate-shared-wishlist-url';

const API_URL = environment.apiUrl;

@Injectable({ providedIn: 'root' })
export class WishListService {
  private http = inject(HttpClient);
  private modalService = inject(ModalService);
  private router = inject(Router);
  #originTypeId = 'a80c8eb3-d79a-48c8-957d-182f0af90254';
  #wishListUrl = `${API_URL}/wishList`;

  addItemWishList(
    detailCatalogId: string,
    wishListId: string
  ): Observable<boolean> {
    const request: AddItemWishListRequest = {
      detailCatalogId,
      wishListId,
      originTypeId: this.#originTypeId,
    };
    return this.http.post(`${this.#wishListUrl}/addItem`, request).pipe(
      map(() => true),
      catchError((err: HttpErrorResponse) => {
        this.modalService.showErrorModal(err);
        return of(false);
      })
    );
  }

  addItemWishListSS(
    detailCatalogId: string,
    wishListId: string,
    userId: string
  ): Observable<boolean> {
    const request: AddItemWishListRequest = {
      detailCatalogId,
      wishListId,
      originTypeId: this.#originTypeId,
      userId: userId
    };
    return this.http.post(`${this.#wishListUrl}/addItemSS`, request).pipe(
      map(() => true),
      catchError((err: HttpErrorResponse) => {
        this.modalService.showErrorModal(err);
        return of(false);
      })
    );
  }

  createWishList(detailCatalogId: string, name: string): Observable<boolean> {
    const request: CreateWishListRequest = {
      detailCatalogId,
      name,
      originTypeId: this.#originTypeId,
    };
    return this.http.post(`${this.#wishListUrl}/create`, request).pipe(
      map(() => true),
      catchError((err: HttpErrorResponse) => {
        this.modalService.showErrorModal(err);
        return of(false);
      })
    );
  }

  createWishListOnly(name: string): Observable<boolean> {
    const request: CreateWishListRequest = {
      name
    };
    return this.http.post(`${this.#wishListUrl}/create_only`, request).pipe(
      map(() => true),
      catchError((err: HttpErrorResponse) => {
        this.modalService.showErrorModal(err);
        return of(false);
      })
    );
  }
  getWishList(): Observable<CatalogOptionResponse[]> {
    return this.http.get<CatalogOptionResponse[]>(`${this.#wishListUrl}/get`);
  }

  getWishListSS(UserId: string): Observable<CatalogOptionResponse[]> {
    const params = new HttpParams().set('UserId', UserId);
    return this.http.get<CatalogOptionResponse[]>(`${this.#wishListUrl}/getSS`,
      { params });
  }

  getWishListDetailSS(
    wishListId: string,UserId: string
  ): Observable<GetWishListDetailResponse[]> {
    const params = new HttpParams()
    .set('wishListId', wishListId)
    .set('UserId', UserId);

    return this.http.get<GetWishListDetailResponse[]>(
      `${this.#wishListUrl}/getDetailSS`,
      { params }
    );
  }

  getWishListDetail(
    wishListId: string
  ): Observable<GetWishListDetailResponse[]> {
    const params = new HttpParams().set('wishListId', wishListId);

    return this.http.get<GetWishListDetailResponse[]>(
      `${this.#wishListUrl}/getDetail`,
      { params }
    );
  }

  removeItem(detailWishListId: string): Observable<boolean> {
    const params = new HttpParams().set('detailWishListId', detailWishListId);

    return this.http
      .post(`${this.#wishListUrl}/removeItem`, null, { params })
      .pipe(
        map(() => true),
        catchError((err: HttpErrorResponse) => {
          this.modalService.showErrorModal(err);
          return of(false);
        })
      );
  }

  removeItemSS(detailWishListId: string, UserId: string): Observable<boolean> {
    const params = new HttpParams().set('detailWishListId', detailWishListId).set('UserId',UserId);

    return this.http
      .post(`${this.#wishListUrl}/removeItemSS`, null, { params })
      .pipe(
        map(() => true),
        catchError((err: HttpErrorResponse) => {
          this.modalService.showErrorModal(err);
          return of(false);
        })
      );
  }

  removeWishList(wishListId: string): Observable<boolean> {
    const params = new HttpParams().set('wishListId', wishListId);

    return this.http.post(`${this.#wishListUrl}/remove`, null, { params }).pipe(
      map(() => true),
      catchError((err: HttpErrorResponse) => {
        this.modalService.showErrorModal(err);
        return of(false);
      })
    );
  }

  moveItemWishList(request: MoveItemWishListRequest): Observable<boolean> {
    return this.http.post(`${this.#wishListUrl}/moveItem`, request).pipe(
      map(() => true),
      catchError((err: HttpErrorResponse) => {
        this.modalService.showErrorModal(err);
        return of(false);
      })
    );
  }

  sharedWishList(request: ShareWishlistRequestInterface): Observable<boolean> {
    return this.http.post(`${this.#wishListUrl}/shared`, request).pipe(
      map(() => true),
      catchError((err: HttpErrorResponse) => {
        this.modalService.showErrorModal(err);
        return of(false);
      })
    );
  }

  validateSharedWishlistUrl(code: string): Observable<ValidateSharedWishlistUrl> {
    return this.http.post<ValidateSharedWishlistUrl>(`${this.#wishListUrl}/validateSharedUrl`, JSON.stringify(code), { headers: { 'Content-Type': 'application/json' }}
    );
  } 

  validateSharedWishlistToken(request: ValidateSharedTokenRequest): Observable<boolean> {
    return this.http.post(`${this.#wishListUrl}/validateSharedToken`, request).pipe(
      map(() => true),
      catchError((err: HttpErrorResponse) => {
        this.modalService.showErrorModal(err);
        return of(false);
      })
    );
  } 
}
